import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import type {
  CollectTiming,
  RefundCustomerFacingReadDTO,
  SalesPointProductCollectionMode,
  TransactionRefundProjectionStatus,
} from 'pi-kiosk-shared';
import type { ApiClient } from '../../shared/api/ApiClient.js';
import { useApiClient } from '../../shared/providers/AppDataProvider.js';
import { useRuntimeConfig } from '../../shared/providers/RuntimeConfigProvider.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { isPlatformChromeTenant } from '../../app/platformChromeTenant.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { fetchPlatformAccountJson } from '../account/platformAccountFetch.js';

/** V2 — session present enables orders queries. */
function isOrdersAccessUsable(
  session: { readonly customerId?: number } | null | undefined,
): boolean {
  return session?.customerId !== undefined;
}

/**
 * G10 SoT (intentional): CollectTiming + SalesPointProductCollectionMode from
 * `pi-kiosk-shared` main entry — not `pi-kiosk-shared/order` (export missing on
 * installed 2.2.55). Locked by `sharedFulfillmentImportPath.test.ts`.
 */
type ProductCollectionMode = SalesPointProductCollectionMode;

/** Wave E — absolute platform orders list (OFFSET). */
export const PLATFORM_ACCOUNT_ORDERS_PATH = '/api/v1/customer-account/orders';

/**
 * Customer list adapter: collectTiming from shared CollectTiming; collection mode
 * from SalesPointProductCollectionMode. Status remains string — intentional
 * divergence from shared CustomerOrderListItem until BE hardens the DTO.
 */
export interface CustomerOrderListItem {
  transactionId: number;
  fulfillmentId: number;
  status: string;
  pickupScheduleType: string;
  promisedPickupAt: string | null;
  collectTiming?: CollectTiming | null;
  pickupHandoffMode?: string | null;
  pickupPointName?: string | null;
  amountMinor: number | null;
  paymentRequired?: boolean;
  /**
   * BE Buy Again CTA gate (COMPLETED + pay-ready + pickup journey).
   * When false/absent for unpaid rows, FE must hide the button.
   */
  buyAgainEligible?: boolean;
  /** Present when backend list DTO includes collection mode. */
  productCollectionMode?: ProductCollectionMode | null;
  currency?: string | null;
  createdAt?: string | null;
  /**
   * Owning shop numeric id — additive G2 (BE list/detail DTO). Prefer over
   * hashed tenantCode for monogram entity id when membership join is absent.
   */
  tenantId?: number;
  /** Wave E fan-out / tenant-tagged rows — BE G7: non-empty on owned list rows. */
  tenantCode?: string;
  tenantName?: string;
}

export interface CustomerOrderDetail {
  transactionId: number;
  version: number;
  status: string;
  pickupScheduleType: string;
  promisedPickupAt: string | null;
  paymentRequired: boolean;
  buyAgainEligible?: boolean;
  /** Optional payment row status when BE includes it; FE hides buy-again unless COMPLETED. */
  paymentStatus?: string | null;
  paymentId: string | null;
  /** Present when backend detail DTO includes charged amount (same as list). */
  amountMinor?: number | null;
  /** ISO currency from order/payment; omit/null → formatter default. */
  currency?: string | null;
  /** List/detail parity — fulfillment row id (CustomerOrderListItemDTO). */
  fulfillmentId?: number;
  collectTiming?: CollectTiming | null;
  pickupPointName?: string | null;
  productCollectionMode?: ProductCollectionMode | null;
  createdAt?: string | null;
  pickupQrVisible?: boolean;
  pickupQrToken?: string | null;
  pickupHandoffMode?: string | null;
  checkoutSubMode?: string | null;
  selfCollectVisible?: boolean;
  informationalPickupAckVisible?: boolean;
  informationalPickupAckCompleted?: boolean;
  /**
   * Owning shop numeric id — additive G2 (same as list). Prefer for monogram
   * when membership join is absent.
   */
  tenantId?: number;
  /** BE G7: non-empty shop slug on owned detail (platform ownershipLookup + tenant). */
  tenantCode?: string;
  tenantName?: string;
  /**
   * Additive V1A refunds on account order detail (GetCustomerOrderDetailUseCase).
   * Omitted on older payloads.
   */
  refunds?: readonly RefundCustomerFacingReadDTO[];
  /** Derived Transaction.refundStatus projection. REQUESTED ≠ returned. */
  refundStatus?: TransactionRefundProjectionStatus | string | null;
  /** Frozen sale lines — snapshot-only (G14/G27); never live catalog names. */
  receiptLines?: readonly {
    readonly productNameSnapshot: string;
    readonly quantity: number;
    readonly unitAmountSnapshot: number;
    readonly lineAmountSnapshot: number;
  }[];
  /** SHIPPING modality — optional while DeliveryPolicy APIs in flux. */
  fulfillmentMode?: 'PICKUP' | 'SHIPPING' | string | null;
  /** Sealed shipping method/policy snapshot when OrderShipment exists. */
  shippingMethodSnapshot?: unknown | null;
  promisedDeliveryByAt?: string | null;
  autoDeliverEligibleAt?: string | null;
  lateComplaintFiledAt?: string | null;
  /** Derived: any late complaint ever filed (open or closed) — permanent CTA hide. */
  lateComplaintEverFiled?: boolean | null;
  /** Tenant confirm handoff kind stamped at MARK_SHIPPED (null until shipped). */
  tenantHandoffKind?:
    | 'DISPATCHED_TO_CARRIER'
    | 'ARRIVED_AT_PARCEL_PICKUP_POINT'
    | 'CUSTOMER_COLLECTED_ASSERTED_BY_TENANT'
    | null;
  shipment?: {
    promisedDeliveryByAt?: string | null;
    autoDeliverEligibleAt?: string | null;
    lateComplaintFiledAt?: string | null;
    tenantHandoffKind?:
      | 'DISPATCHED_TO_CARRIER'
      | 'ARRIVED_AT_PARCEL_PICKUP_POINT'
      | 'CUSTOMER_COLLECTED_ASSERTED_BY_TENANT'
      | null;
  } | null;
}

export interface CustomerPickupQrDetail {
  visible: boolean;
  token: string | null;
  verifyUrl: string | null;
}

export interface CustomerSelfCollectResult {
  collected: true;
}

export interface LinkCustomerOrderResult {
  transactionId: number;
  linked: boolean;
  conflict: boolean;
  alreadyLinkedTo: number | null;
}

export function createOrdersApi(api: ApiClient) {
  return {
    fetchCustomerOrders: (params?: {
      limit?: number;
      offset?: number;
    }): Promise<{ items: CustomerOrderListItem[]; total: number }> =>
      api.request('/customer-account/orders', {
        query: {
          ...(params?.limit !== undefined ? { limit: params.limit } : {}),
          ...(params?.offset !== undefined ? { offset: params.offset } : {}),
        },
      }),

    fetchCustomerOrderDetail: (transactionId: number): Promise<CustomerOrderDetail> =>
      api.request(`/customer-account/orders/${transactionId}`),

    fetchCustomerOrderPickupQr: (transactionId: number): Promise<CustomerPickupQrDetail> =>
      api.request(`/customer-account/orders/${transactionId}/pickup-qr`),

    selfCollectOrder: (
      transactionId: number,
      body: { version: number }
    ): Promise<CustomerSelfCollectResult> =>
      api.request(`/customer-account/orders/${transactionId}/self-collect`, {
        method: 'POST',
        body,
      }),

    ackInformationalPickup: (
      transactionId: number,
      body: { version: number }
    ): Promise<{ acknowledged: true; informationalPickupAckCompleted: true }> =>
      api.request(`/customer-account/orders/${transactionId}/ack-informational-pickup`, {
        method: 'POST',
        body,
      }),

    /** CUSTOMER_ACK_RECEIPT — operational receipt only (S11 ≠ waiver); sole V1 DELIVERED fire. */
    ackShippingReceipt: (
      transactionId: number,
      body: { version: number },
      idempotencyKey: string
    ): Promise<{
      status: 'DELIVERED';
      version: number;
      deliveredAt: string;
      operationalReceiptOnly: true;
      waiverOfRights: false;
    }> =>
      api.request(`/customer-account/orders/${transactionId}/ack-shipping-receipt`, {
        method: 'POST',
        body,
        idempotencyKey,
      }),

    /** REPORT_PROBLEM — case-only; does not mutate fulfillment status. */
    reportShippingProblem: (
      transactionId: number,
      body: { orderFulfillmentId: number; customerDescription: string },
      idempotencyKey: string
    ): Promise<unknown> =>
      api.request(`/customer-account/orders/${transactionId}/report-shipping-problem`, {
        method: 'POST',
        body,
        idempotencyKey,
      }),

    /**
     * G10 — after-sale intake (WITHDRAWAL|COMPLAINT|DELIVERY_PROBLEM).
     * Matches B03 after-sale links; not shippingLightEnabled-gated.
     */
    intakeAfterSale: (
      transactionId: number,
      body: {
        caseType: 'WITHDRAWAL' | 'COMPLAINT' | 'DELIVERY_PROBLEM';
        customerDescription: string;
        requestedRemedy?: 'REPAIR' | 'REPLACEMENT' | 'REFUND' | 'PRICE_REDUCTION' | 'OTHER';
        contactSnapshot?: Record<string, unknown>;
        orderFulfillmentId?: number | null;
      },
      idempotencyKey: string
    ): Promise<{ id: string } | unknown> =>
      api.request(`/customer-account/orders/${transactionId}/after-sale`, {
        method: 'POST',
        body,
        idempotencyKey,
      }),

    /** N9 inventory — POST /customer-account/orders/:transactionId/link */
    linkOrder: (transactionId: number): Promise<LinkCustomerOrderResult> =>
      api.request(`/customer-account/orders/${transactionId}/link`, {
        method: 'POST',
        body: {},
      }),
  };
}

export type OrdersApi = ReturnType<typeof createOrdersApi>;

export function useOrdersApi(): OrdersApi {
  const api = useApiClient();
  return useMemo(() => createOrdersApi(api), [api]);
}

export function useCustomerOrders(params?: {
  limit?: number;
  offset?: number;
}): UseQueryResult<{
  items: CustomerOrderListItem[];
  total: number;
}> {
  const tenantCode = useTenantCode();
  const ordersApi = useOrdersApi();
  const config = useRuntimeConfig();
  const { session } = useCustomerSession();
  const customerId = session?.customerId;
  const accessUsable = isOrdersAccessUsable(session);
  const platformChrome = isPlatformChromeTenant(tenantCode);
  const limit = params?.limit ?? 20;
  const offset = params?.offset ?? 0;

  return useQuery({
    queryKey: ['customer-orders', tenantCode, customerId ?? -1, limit, offset],
    queryFn: ({ signal }) => {
      // Wave E: platform hub — absolute OFFSET fan-out (BAN orders cursor).
      if (platformChrome) {
        return fetchPlatformAccountJson<{
          items: CustomerOrderListItem[];
          total: number;
        }>({
          apiBaseUrl: config.apiBaseUrl,
          path: PLATFORM_ACCOUNT_ORDERS_PATH,
          // Spec A: cookie jar only — never memory Bearer + omit (drops HttpOnly).
          accessToken: null,
          signal,
          query: { limit, offset },
        });
      }
      return ordersApi.fetchCustomerOrders({ limit, offset });
    },
    enabled: customerId !== undefined && accessUsable,
  });
}

export function useCustomerOrderDetail(
  transactionId: number
): UseQueryResult<CustomerOrderDetail> {
  const tenantCode = useTenantCode();
  const ordersApi = useOrdersApi();
  const config = useRuntimeConfig();
  const { session } = useCustomerSession();
  const accessUsable = isOrdersAccessUsable(session);
  const platformChrome = isPlatformChromeTenant(tenantCode);

  return useQuery({
    queryKey: ['customer-order', tenantCode, transactionId],
    queryFn: ({ signal }) => {
      if (platformChrome) {
        return fetchPlatformAccountJson<CustomerOrderDetail>({
          apiBaseUrl: config.apiBaseUrl,
          path: `${PLATFORM_ACCOUNT_ORDERS_PATH}/${transactionId}`,
          accessToken: null,
          signal,
        });
      }
      return ordersApi.fetchCustomerOrderDetail(transactionId);
    },
    enabled: Number.isFinite(transactionId) && accessUsable,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data === undefined) {
        return false;
      }
      if (data.refundStatus === 'REQUESTED' || data.refundStatus === 'PROCESSING') {
        return 5_000;
      }
      const refunds = data.refunds ?? [];
      const inFlight = refunds.some((row) => row.customerStatus === 'processing');
      return inFlight ? 5_000 : false;
    },
    refetchIntervalInBackground: false,
  });
}

export function useCustomerOrderPickupQr(
  transactionId: number,
  enabled = true
): UseQueryResult<CustomerPickupQrDetail> {
  const tenantCode = useTenantCode();
  const ordersApi = useOrdersApi();
  const config = useRuntimeConfig();
  const { session } = useCustomerSession();
  const accessUsable = isOrdersAccessUsable(session);
  const platformChrome = isPlatformChromeTenant(tenantCode);

  return useQuery({
    queryKey: ['customer-order-pickup-qr', tenantCode, transactionId],
    queryFn: ({ signal }) => {
      if (platformChrome) {
        return fetchPlatformAccountJson<CustomerPickupQrDetail>({
          apiBaseUrl: config.apiBaseUrl,
          path: `${PLATFORM_ACCOUNT_ORDERS_PATH}/${transactionId}/pickup-qr`,
          accessToken: null,
          signal,
        });
      }
      return ordersApi.fetchCustomerOrderPickupQr(transactionId);
    },
    enabled: enabled && Number.isFinite(transactionId) && accessUsable,
    staleTime: 30_000,
  });
}

export function useCustomerSelfCollectOrder(): UseMutationResult<
  CustomerSelfCollectResult,
  Error,
  { transactionId: number; version: number; tenantCode?: string }
> {
  const ordersApi = useOrdersApi();
  return useMutation({
    mutationFn: async ({ transactionId, version, tenantCode: rowTenantCode }) => {
      void rowTenantCode;
      return ordersApi.selfCollectOrder(transactionId, { version });
    },
  });
}

export function useLinkCustomerOrder(): UseMutationResult<
  LinkCustomerOrderResult,
  Error,
  { transactionId: number }
> {
  const ordersApi = useOrdersApi();
  return useMutation({
    mutationFn: ({ transactionId }) => ordersApi.linkOrder(transactionId),
  });
}

export function useCustomerAckInformationalPickup(): UseMutationResult<
  { acknowledged: true; informationalPickupAckCompleted: true },
  Error,
  { transactionId: number; version: number; tenantCode?: string }
> {
  const tenantCode = useTenantCode();
  const ordersApi = useOrdersApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ transactionId, version, tenantCode: rowTenantCode }) => {
      void rowTenantCode;
      return ordersApi.ackInformationalPickup(transactionId, { version });
    },
    onSuccess: (_data, { transactionId }) => {
      void queryClient.invalidateQueries({
        queryKey: ['customer-order', tenantCode, transactionId],
      });
    },
  });
}

function newShippingIdempotencyKey(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function useCustomerAckShippingReceipt(): UseMutationResult<
  {
    status: 'DELIVERED';
    version: number;
    deliveredAt: string;
    operationalReceiptOnly: true;
    waiverOfRights: false;
  },
  Error,
  { transactionId: number; version: number }
> {
  const tenantCode = useTenantCode();
  const ordersApi = useOrdersApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ transactionId, version }) =>
      ordersApi.ackShippingReceipt(
        transactionId,
        { version },
        newShippingIdempotencyKey('ship-ack'),
      ),
    onSuccess: (_data, { transactionId }) => {
      void queryClient.invalidateQueries({
        queryKey: ['customer-order', tenantCode, transactionId],
      });
    },
  });
}

export function useCustomerReportShippingProblem(): UseMutationResult<
  unknown,
  Error,
  { transactionId: number; orderFulfillmentId: number; customerDescription: string }
> {
  const tenantCode = useTenantCode();
  const ordersApi = useOrdersApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ transactionId, orderFulfillmentId, customerDescription }) =>
      ordersApi.reportShippingProblem(
        transactionId,
        { orderFulfillmentId, customerDescription },
        newShippingIdempotencyKey('ship-problem'),
      ),
    // G6 — invalidate detail so lateComplaintFiledAt / lateComplaintEverFiled
    // permanent-hide survives remount (not local-only reportDone).
    onSuccess: (_data, { transactionId }) => {
      void queryClient.invalidateQueries({
        queryKey: ['customer-order', tenantCode, transactionId],
      });
    },
  });
}
